
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";

interface AuthContextType {
  user: User | null;
  signOut: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  signOut: async () => {},
  isLoading: true,
});

export const useAuth = () => {
  return useContext(AuthContext);
};

interface AuthProviderProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

export const AuthProvider = ({ children, requireAuth = false }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Use ref to capture requireAuth value to avoid dependency issues
  const requireAuthRef = useRef(requireAuth);
  useEffect(() => {
    requireAuthRef.current = requireAuth;
  }, [requireAuth]);

  // Memoize auth-related route check to prevent recalculation
  const isAuthPage = useMemo(() => {
    return location.pathname === '/login' || 
           location.pathname === '/signup' ||
           location.pathname === '/auth/callback';
  }, [location.pathname]);

  // Stable navigation function - remove user dependency to prevent loops
  const navigateToPosits = useCallback(() => {
    // Don't depend on user state to prevent infinite loops
    if (isAuthPage) {
      navigate('/posits', { replace: true });
    }
  }, [isAuthPage, navigate]);

  const navigateToLogin = useCallback(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  // Stable signOut function
  const signOut = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Clear local session regardless of server response
      await supabase.auth.signOut({ scope: 'local' });
      
      // Always navigate and show success message
      navigate('/', { replace: true });
      toast({
        title: "Signed out successfully",
        description: "You have been logged out of your account.",
      });
    } catch (error: any) {
      
      // Even if server logout fails, clear local session and continue
      try {
        await supabase.auth.signOut({ scope: 'local' });
        navigate('/', { replace: true });
        toast({
          title: "Signed out successfully",
          description: "You have been logged out of your account.",
        });
      } catch (fallbackError) {
        // Last resort - manual cleanup
        setUser(null);
        navigate('/', { replace: true });
        toast({
          title: "Signed out",
          description: "Session cleared locally.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, toast]);

  // Initialize auth only once - remove toast and navigation callbacks from dependencies
  useEffect(() => {
    if (initialized) return;
    
    
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        setUser(session?.user ?? null);
        
        if (session?.user && isAuthPage) {
          navigate('/posits', { replace: true });
        } else if (requireAuth && !session?.user) {
          navigate('/login', { replace: true });
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Authentication Error", 
          description: "There was a problem checking your login status. Please try again.",
        });
      } finally {
        setIsLoading(false);
        setInitialized(true);
      }
    };

    initializeAuth();
  }, [initialized, requireAuth, isAuthPage, navigate]); // Remove callbacks to prevent loops

  // Handle auth state changes - set up only once, no dependencies on changing callbacks
  useEffect(() => {
    
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      
      setUser(session?.user ?? null);
      
      // Handle navigation directly to avoid dependency loops
      if (event === 'SIGNED_IN' && session?.user) {
        // Only redirect if on auth page - check current location at time of event
        const currentPath = window.location.pathname;
        if (currentPath === '/login' || currentPath === '/signup' || currentPath === '/auth/callback') {
          navigate('/dashboard/posits', { replace: true });
        }
      } else if (event === 'SIGNED_OUT') {
        navigate('/', { replace: true });
      } else if (requireAuthRef.current && !session?.user && event !== 'INITIAL_SESSION') {
        navigate('/login', { replace: true });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []); // No dependencies to prevent re-setup

  const value = useMemo(() => ({
    user,
    signOut,
    isLoading,
  }), [user, signOut, isLoading]);

  // Don't render children until auth is initialized (prevents flash)
  if (isLoading && !initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
